import { FormInput, Modal } from "@general-backoffice/core";
import React from "react";
import { useTranslation } from "react-i18next";

import { useFormHandlers } from "../../../hooks/strapi";

interface ReferralCodeData {
  id?: number | string;
  name?: string | null;
  code?: string;
  referrer?: { email?: string } | null;
  [key: string]: unknown;
}

interface CreateReferralCodeModalProps<T extends ReferralCodeData = ReferralCodeData> {
  referralCode: T | null;
  setReferralCode: React.Dispatch<React.SetStateAction<T | null>>;
  close: () => void;
  create: () => void | Promise<void>;
  isLoading: boolean;
}

const CreateReferralCodeModal = <T extends ReferralCodeData = ReferralCodeData>({
  referralCode,
  setReferralCode,
  close,
  create,
  isLoading,
}: CreateReferralCodeModalProps<T>) => {
  const { id, name, code, referrer } = referralCode || {};
  const { handleInputEvent } = useFormHandlers(setReferralCode);
  const { t } = useTranslation("referrer", { keyPrefix: "partner.createReferralCodeModal" });

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    await create();
  };

  const areLoading = isLoading;
  return (
    <Modal.FormContainer
      title={id ? t("titleUpdate", { name }) : t("titleCreate")}
      isOpen={!!referralCode}
      toggleOpen={close}
      closeText={t("closeText")}
      buttonSave={{ children: t("saveText"), disabled: areLoading }}
      isLoading={areLoading}
      onSubmit={handleSubmit}
    >
      {referrer && <FormInput.Input label={t("referrerLabel")} value={referrer?.email} disabled />}

      <FormInput.Input
        name="name"
        label={t("nameLabel")}
        value={name ?? ""}
        onChange={handleInputEvent}
        disabled={areLoading}
        required
      />

      <FormInput.Input
        name="code"
        label={t("codeLabel")}
        value={code}
        onChange={handleInputEvent}
        disabled={areLoading}
        maxLength={6}
        minLength={6}
        pattern="^[a-zA-Z0-9]*$"
        description={t("codeDescription")}
        required
      />
    </Modal.FormContainer>
  );
};

export default CreateReferralCodeModal;
